// AI Analysis section for PDF report
import { PDF_CONFIG, getContentWidth } from '../config.js';
import { renderSectionTitle, checkPageBreak, drawBox, wrapText } from '../helpers/layoutUtils.js';

/**
 * Render the AI Analysis section
 * @param {jsPDF} pdf - The PDF document
 * @param {string} aiExplanation - AI-generated analysis text
 * @param {number} startY - Starting Y position
 * @returns {number} - Y position after section
 */
export const renderAIAnalysis = (pdf, aiExplanation, startY) => {
  const { margins, colors, fonts, spacing } = PDF_CONFIG;
  const contentWidth = getContentWidth();

  if (!aiExplanation) {
    return startY;
  }

  // Start on a new page for AI analysis
  pdf.addPage();
  let y = margins.top + 40; // Account for header

  y = renderSectionTitle(pdf, 'AI-Powered Analysis', y);

  // Intro box
  drawBox(pdf, margins.left, y, contentWidth, 18, [240, 249, 255], colors.blue);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...colors.darkText);
  pdf.text('The following analysis was generated by Claude AI to provide insights into your', margins.left + 4, y + 7);
  pdf.text('portfolio rebalancing needs. This is for informational purposes only.', margins.left + 4, y + 13);

  y += 25;

  // Parse and render the AI explanation
  // Handle markdown-style formatting
  const paragraphs = aiExplanation.split(/\n\n+/);

  paragraphs.forEach(para => {
    para = para.trim();
    if (!para) return;

    y = checkPageBreak(pdf, y, 20);

    // Check if it's a header (starts with ** or #)
    if (para.startsWith('**') && para.includes(':**')) {
      // Bold header line
      const headerMatch = para.match(/^\*\*(.+?):\*\*(.*)$/);
      if (headerMatch) {
        const headerText = headerMatch[1].trim();
        const bodyText = headerMatch[2].trim();

        // Render header
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...colors.navyHeader);
        pdf.text(headerText, margins.left, y);
        y += 6;

        // Render body if present
        if (bodyText) {
          pdf.setFontSize(fonts.body);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.darkText);

          const lines = wrapText(pdf, bodyText);
          lines.forEach(line => {
            y = checkPageBreak(pdf, y, 5);
            pdf.text(line, margins.left, y);
            y += 4.5;
          });
        }

        y += 4;
        return;
      }
    }

    // Check for section headers (Paragraph 1, Paragraph 2, etc.)
    if (para.match(/^\*\*Paragraph \d/i) || para.startsWith('**')) {
      // Extract header text
      const cleanHeader = para.replace(/\*\*/g, '').split('\n')[0].trim();

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...colors.navyHeader);
      pdf.text(cleanHeader, margins.left, y);
      y += 7;

      // Get remaining text after header
      const remainingLines = para.split('\n').slice(1).join(' ').replace(/\*\*/g, '').trim();
      if (remainingLines) {
        pdf.setFontSize(fonts.body);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...colors.darkText);

        const lines = wrapText(pdf, remainingLines);
        lines.forEach(line => {
          y = checkPageBreak(pdf, y, 5);
          pdf.text(line, margins.left, y);
          y += 4.5;
        });
      }

      y += 4;
      return;
    }

    // Regular paragraph
    const cleanPara = para.replace(/\*\*/g, '').trim();

    pdf.setFontSize(fonts.body);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...colors.darkText);

    const lines = wrapText(pdf, cleanPara);
    lines.forEach(line => {
      y = checkPageBreak(pdf, y, 5);
      pdf.text(line, margins.left, y);
      y += 4.5;
    });

    y += spacing.paragraphGap;
  });

  // Key takeaways box (if we can identify recommendations)
  const recommendations = extractRecommendations(aiExplanation);
  if (recommendations.length > 0) {
    y = checkPageBreak(pdf, y, 40);
    y += 5;

    // Box header
    pdf.setFillColor(...colors.navyHeader);
    pdf.rect(margins.left, y, contentWidth, 8, 'F');
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...colors.white);
    pdf.text('Key Takeaways', margins.left + 4, y + 5.5);
    y += 8;

    // Box body
    const boxBodyHeight = recommendations.length * 7 + 6;
    drawBox(pdf, margins.left, y, contentWidth, boxBodyHeight, colors.lightGray);

    y += 5;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...colors.darkText);

    recommendations.forEach(rec => {
      pdf.text(`•  ${rec}`, margins.left + 4, y);
      y += 6;
    });

    y += 3;
  }

  return y + spacing.sectionGap;
};

/**
 * Extract key recommendations from AI analysis text
 * @param {string} text - AI explanation text
 * @returns {string[]} - Array of recommendations
 */
const extractRecommendations = (text) => {
  const recommendations = [];

  // Look for patterns that indicate recommendations
  const patterns = [
    /recommend(?:ed|s|ation)?\s+(?:to\s+)?(.{20,80}?)(?:\.|$)/gi,
    /(?:should|could|consider)\s+(.{20,80}?)(?:\.|$)/gi,
    /(?:action|step):\s*(.{20,80}?)(?:\.|$)/gi,
  ];

  patterns.forEach(pattern => {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      const rec = match[1]?.trim();
      if (rec && rec.length > 15 && rec.length < 100) {
        // Capitalize first letter
        const formatted = rec.charAt(0).toUpperCase() + rec.slice(1);
        if (!recommendations.includes(formatted)) {
          recommendations.push(formatted);
        }
      }
    }
  });

  return recommendations.slice(0, 4); // Max 4 recommendations
};
